import { CircularProgress } from '@mui/material';
import { default as classnames, default as cx } from 'classnames';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import Tooltip from 'js/components/elements/mui/Tooltip';
import ResizableHorizontalLayout from 'js/components/elements/ResizableHorizontalLayout';
import { ResizableVerticalLayout } from 'js/components/elements/ResizableVerticalLayout';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import {
  getFeatureFlagValue,
  isSourceFormRuntimeSelectionEnabled,
} from 'new/constants/featureFlags';
import { SchemaEditorViewModeType } from 'js/constants/schemaEditor';
import { SUPPORT_KEY_SOURCE_FORM_DB } from 'js/constants/supportPageKeys';
import JSONUtils from 'js/lib/JSONUtils';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import { debounce, filter, find, forEach, get, isEmpty, isEqual, isNil, map, pick } from 'lodash';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh.svg';
import { Box } from 'new/elements/Box';
import { EmptyView } from 'new/elements/EmptyView';
import { ExpandableSearchBar } from 'new/elements/ExpandableSearchBar';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu';
import { SearchableProvider } from 'new/elements/SearchableProvider';
import { SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { Stack } from 'new/elements/Stack';
import { Button } from 'new/elements/support/Button';
import { UiMode } from 'new/constants/uiMode';
import Typography from 'new/elements/TypographySupport';
import { buildAutoSelectSql } from 'new/utils/sqlAutoQuery';
import { tryCatchAsync } from 'new/utils/TryCatch';
import React from 'react';
import Constants from '../../../../Constants';
import UIUtils from '../../../../lib/UIUtils';
import LoadableMonacoEditor from '../../../elements/editors/monaco/LoadableMonacoEditor';
import AdvancedConfiguration from '../AdvancedConfiguration';
import DataSourceDetails from '../DataSourceDetails/DataSourceDetails';
import MonitoringInterval from '../MonitoringInterval/MonitoringInterval';
import SelectedDatabaseInputs from '../SelectedDatabaseInputs/SelectedDatabaseInputs';
import DatabaseList from './DatabaseList';
import DatabaseSamples from './DatabaseSamples';
import { SchemaTreePanel } from './redesign/SchemaTreePanel';
import { SourceModeToggle } from './redesign/SourceModeToggle';
import { SourceTablePreview } from './redesign/SourceTablePreview';
import { SqlEditorPanel } from './redesign/SqlEditorPanel';
import './redesign/SourceTableRedesign.scss';

import RuntimeSelection from 'new/modules/settings/capsules/components/RuntimeSelection/RuntimeSelection';
import RuntimeSelectionDescription from 'new/modules/settings/capsules/components/RuntimeSelection/RuntimeSelectionDescription';
import '../ConfigureAPI.scss';
import '../SelectBucket.scss';
import './DatabaseConfigurator.scss';

const initialState = {
  databasesIds: [],
  databases: {},
  tables: {},
  selectedDatabaseId: null,
  expandedTableId: null,
  tablesForDatabase: {},
};

const ResizableVerticalLayoutConfig = {
  forceHeight: true,
  initialSizes: ['40%', '60%'],
  minSizes: [150, 150],
  maxSizes: [400, 400],
  dragBarVariant: 'cloudy-gray',
};

const controls = [
  {
    label: 'Table Mode',
    value: 'Default',
  },
  {
    label: 'Query Mode',
    value: 'Query',
  },
];

const loadableMonacoEditorOptions = {
  minimap: { enabled: false },
};

const TABLE_CONFIG_KEYS = [
  'incrementing.column.name',
  'timestamp.column.name',
  'incrementing.load.from',
  'timestamp.load.from',
];

// "Recent" tables in the tree (auto-added when a table's samples are fetched,
// plus manual pins) are capped and newest-first so they stay handy without
// crowding out the database tree.
const MAX_RECENT_TABLES = 5;

function getEffectiveQueryMode(queryModeOnly, sourceConfig) {
  return queryModeOnly === true ? 'Query' : sourceConfig?.db_query_mode || 'Default';
}

class DatabaseConfigurator extends React.Component {
  state = {
    mode: getEffectiveQueryMode(this.props.queryModeOnly, this.props.sourceConfig),
    databasesIds: [],
    databases: {},
    tables: {},
    selectedDatabaseId: null,
    expandedTableId: null,
    tablesForDatabase: {},
    samplesData: null,
    editedSamples: null,
    runtimeSectionVisible: true,
    // Redesigned source-table screen
    pinnedTableIds: [],
    // A query is "custom" once the user edits it (or one was already saved);
    // otherwise we keep it synced to the auto-generated SELECT for the table.
    isCustomQuery: !!this.props.sourceConfig?.query,
  };

  _debouncedSetQuery = debounce((query) => {
    this.updateSourceConfigField('query', query);
  }, 200);

  // Auto-generated preview query for the selected table.
  _buildAutoSql = () => {
    const { selectedTable, sourceConfig, selectedConnection } = this.props;
    return buildAutoSelectSql(
      selectedTable?.name || sourceConfig?.table,
      selectedTable?.database || sourceConfig?.database,
      selectedConnection,
    );
  };

  // The table referenced by the current query's FROM clause (e.g. "db.table"),
  // used to label the Query-mode preview from what was actually queried rather
  // than whatever is selected in the tree.
  _getQueriedTableRef = () => {
    const query = this.props.sourceConfig?.query || '';
    const match = query.match(/\bfrom\s+([`"[\]A-Za-z0-9_.$]+)/i);
    if (!match) return '';
    return match[1].replace(/[`"[\]]/g, '');
  };

  // Columns of the queried table (matched in the probe tree by name), so the
  // Query-mode preview can show type / PK metadata for a simple SELECT.
  _getQueriedTableColumns = (ref) => {
    if (!ref) return [];
    const tableName = ref.split('.').pop()?.toLowerCase();
    if (!tableName) return [];
    const table = find(this.state.tables, (t) => t?.name?.toLowerCase() === tableName);
    return table?.columns || [];
  };

  // Keep sourceConfig.query in sync with the auto-generated SQL while the user
  // has not customized it, so Query mode and the backend agree. Runs after
  // props settle (componentDidMount/Update) to avoid stale-merge races.
  _syncAutoQuery = () => {
    const mode = getEffectiveQueryMode(this.props.queryModeOnly, this.props.sourceConfig);
    if (mode !== 'Query' || this.state.isCustomQuery) return;
    const auto = this._buildAutoSql();
    if (auto && this.props.sourceConfig?.query !== auto) {
      this.updateSourceConfigField('query', auto);
    }
  };

  _onQueryChange = (value) => {
    const next = value ?? '';
    // Programmatic/auto value pushes match the generated SQL — not a user edit.
    if (!this.state.isCustomQuery && next === this._buildAutoSql()) return;
    if (!this.state.isCustomQuery) this.setState({ isCustomQuery: true });
    this._debouncedSetQuery(next);
  };

  _onResetQuery = () => {
    const auto = this._buildAutoSql();
    this.setState({ isCustomQuery: false }, () => {
      if (auto && this.props.sourceConfig?.query !== auto) {
        this.updateSourceConfigField('query', auto);
      }
    });
  };

  // Manual pin toggle: remove if present, else add to the front (capped).
  _onTogglePin = (tableId) => {
    this.setState((s) => ({
      pinnedTableIds: s.pinnedTableIds.includes(tableId)
        ? s.pinnedTableIds.filter((id) => id !== tableId)
        : [tableId, ...s.pinnedTableIds].slice(0, MAX_RECENT_TABLES),
    }));
  };

  // Mark a table as recently explored (newest first, capped, deduped). Used
  // when the user fetches samples / runs a query for a table.
  _markRecentTable = (tableId) => {
    if (!tableId) return;
    this.setState((s) => {
      const next = [tableId, ...s.pinnedTableIds.filter((id) => id !== tableId)].slice(
        0,
        MAX_RECENT_TABLES,
      );
      return { pinnedTableIds: next };
    });
  };

  // In the redesigned tree, expand the first database by default so tables are
  // visible immediately instead of an empty tree. Only runs once, and not when
  // a database/table is already selected (e.g. editing an existing source).
  _didAutoExpandDatabase = false;
  // Always open one database on first entry so its tables are visible (never
  // leave a database name collapsed/empty). Opens the already-selected database
  // when there is one (e.g. a credential-preset database, as with Snowflake),
  // otherwise the first one. Runs once.
  _maybeAutoExpandDatabase = () => {
    if (this.props.parentDataSet) return; // redesign path only
    if (this._didAutoExpandDatabase) return;
    if (this.props.selectedTable) return; // a specific table is already targeted

    const { selectedDatabaseId, databasesIds, databases } = this.state;
    const targetId =
      selectedDatabaseId && databases?.[selectedDatabaseId]
        ? selectedDatabaseId
        : databasesIds?.[0];
    if (!targetId || !databases?.[targetId]) return;

    this._didAutoExpandDatabase = true;
    if (selectedDatabaseId === targetId) {
      // Already selected/open — just probe its tables (no-op if already loaded).
      this.probeNode(targetId);
    } else {
      // Select it (opens it) and probe its tables.
      this.selectDatabase(targetId);
    }
  };

  // Lazily fetch a table's columns for the hover preview without selecting it.
  // probeNode no-ops if the node is already scanned.
  _probeTableForPreview = (tableId) => {
    this.probeNode(tableId);
  };

  // Adds database / table / column names to the Query-mode editor's
  // autocomplete (SQL keywords come from Monaco's built-in 'sql' provider).
  //
  // Monaco completion providers are registered globally per language, so we
  // register exactly ONCE per Monaco instance — guarded by a flag on the
  // `monaco` object so it survives editor remounts, StrictMode double-mounts and
  // hot reloads (which previously stacked duplicate providers). The provider
  // reads a live data getter, also stored on `monaco` and refreshed on every
  // mount, so suggestions always reflect the latest probe tree.
  _handleSqlEditorMount = (editor, monaco) => {
    if (!monaco?.languages) return undefined;

    monaco.__nexlaDbSqlData = () => {
      // Once this screen unmounts, contribute nothing — so the (undisposed,
      // global) 'sql' provider never leaks DB-source names into unrelated SQL
      // editors elsewhere in the app.
      if (this._sqlEditorUnmounted) return null;
      const { databasesIds, databases, tables } = this.state;
      return {
        databasesIds: databasesIds || [],
        databases: databases || {},
        tables: tables || {},
        selectedTable: this.props.selectedTable,
      };
    };

    if (monaco.__nexlaDbSqlRegistered) return undefined;
    monaco.__nexlaDbSqlRegistered = true;

    const Kind = monaco.languages.CompletionItemKind;
    monaco.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: ['.', ' '],
      provideCompletionItems(model, position) {
        const data = monaco.__nexlaDbSqlData?.();
        if (!data) return { suggestions: [] };

        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };
        const suggestions = [];
        const seen = new Set();
        // sortKey '0' sorts above Monaco's built-in keyword suggestions.
        const add = (label, kind, detail, sortKey = '0') => {
          const key = `${kind}:${label}`;
          if (seen.has(key)) return;
          seen.add(key);
          suggestions.push({
            label,
            insertText: label,
            kind,
            detail,
            range,
            sortText: `${sortKey}_${label}`,
          });
        };
        const addDatabases = () =>
          forEach(data.databasesIds, (dbId) => {
            const db = data.databases[dbId];
            if (db?.name) add(db.name, Kind.Module, 'database');
          });
        const addTables = () =>
          forEach(data.tables, (t) => {
            if (t?.name) add(t.name, Kind.Struct, t.database ? `table · ${t.database}` : 'table');
          });
        const addColumns = (cols, tableName) =>
          forEach(cols, (c) => {
            if (c?.name) add(c.name, Kind.Field, tableName ? `column · ${tableName}` : 'column');
          });
        const addAllColumns = () => {
          addColumns(data.selectedTable?.columns, data.selectedTable?.name);
          forEach(data.tables, (t) => addColumns(t.columns, t.name));
        };

        // Qualified reference: "<name>." → suggest that scope only.
        const linePrefix = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });
        const qualified = linePrefix.match(/([A-Za-z0-9_]+)\.[A-Za-z0-9_]*$/);
        if (qualified) {
          const qname = qualified[1];
          const db = Object.values(data.databases).find((d) => d?.name === qname);
          if (db) {
            forEach(data.tables, (t) => {
              if (t?.database === db.name && t?.name)
                add(t.name, Kind.Struct, `table · ${db.name}`);
            });
            return { suggestions };
          }
          const tbl = Object.values(data.tables).find((t) => t?.name === qname);
          if (tbl) {
            addColumns(tbl.columns, tbl.name);
            return { suggestions };
          }
          return { suggestions: [] };
        }

        // Otherwise infer intent from the last SQL clause keyword before the
        // cursor: after FROM/JOIN we want databases & tables; after
        // SELECT/WHERE etc. we want columns; at statement start we add nothing
        // and let the built-in keyword suggestions surface (e.g. SELECT).
        const prefixAll = model
          .getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          })
          .toLowerCase();
        const tokens = prefixAll.match(/[a-z_]+/g) || [];
        let context = 'default';
        for (let i = tokens.length - 1; i >= 0; i--) {
          const t = tokens[i];
          if (['from', 'join', 'into', 'update'].includes(t)) {
            context = 'table';
            break;
          }
          if (['select', 'where', 'on', 'set', 'having', 'by', 'and', 'or'].includes(t)) {
            context = 'column';
            break;
          }
        }

        if (context === 'table') {
          addDatabases();
          addTables();
        } else if (context === 'column') {
          addAllColumns();
        }

        return { suggestions };
      },
    });

    return undefined;
  };

  _handleRuntimeVisibilityChange = (visible) => {
    this.setState({ runtimeSectionVisible: visible });
  };

  _handleSamplesChanged = (editedSamples) => {
    this.setState({ editedSamples });
    if (this.props.onEditedSamplesChanged) {
      this.props.onEditedSamplesChanged(editedSamples);
    }
  };

  componentDidMount() {
    //Fetch probe tree for this data source
    if (this.props.selectedCredentialId) {
      this.onRequestTree();
    } else {
      this.setState({ ...initialState });
    }
    if (this.props.probeData) {
      this.initializeState(this.props);
    }
    this._syncAutoQuery();
    this._maybeAutoExpandDatabase();
    this.props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_DB);
  }

  componentWillUnmount() {
    this._sqlEditorUnmounted = true;
    this.props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_DB);
  }

  componentDidUpdate(prevProps, prevState) {
    let { selectedCredentialId, selectedTable, probeSample, parentDataSet, parentDataSetSamples } =
      this.props;
    const isApiServerFlow = !!parentDataSet;
    const selectedQueryMode = getEffectiveQueryMode(
      this.props.queryModeOnly,
      this.props.sourceConfig,
    );

    if (prevProps.selectedCredentialId !== selectedCredentialId) {
      this.props.onRequestProbeTree();
    }

    // Drop the previous table's samples when the user selects a different table
    // in the tree (in either mode). Tree hover/probe doesn't change the selected
    // table, so this only fires on an explicit click — not on a flicker.
    if (prevProps.selectedTable?.id !== selectedTable?.id && this.state.samplesData) {
      this.setState({ samplesData: null });
    }

    const tId = this.props.selectedTable && this.props.selectedTable.id;

    const samples = get(probeSample, '[0].response.output');

    if (
      !!samples &&
      !isEqual(probeSample, prevProps.probeSample) &&
      (selectedQueryMode === 'Query' || this.prevProps?.selectedTable?.id !== selectedTable?.id)
    ) {
      this.setState({ samplesData: samples });
    }

    // Refresh the selected table's columns once its probe scan completes, in
    // either mode, so selectedTable.columns is populated and the preview works
    // immediately after switching Query -> Table. This is safe in Query mode
    // because that preview is driven by the executed query, not selectedTable,
    // and re-selecting the same table id doesn't disturb it.
    if (
      tId &&
      prevState.tables[tId] &&
      this.state.tables[tId] &&
      !prevState.tables[tId].scanned &&
      this.state.tables[tId].scanned
    ) {
      this.selectTable(tId);
    }
    if (this.props.probeData && this.props.probeData !== prevProps.probeData) {
      this.initializeState(this.props);
    }
    this._syncAutoQuery();
    this._maybeAutoExpandDatabase();
  }

  initializeState = (props) => {
    let databasesIds = [],
      databases = {},
      tables = {},
      tablesForDatabase = {};
    forEach(props.probeData, (d) => {
      let database = {
        id: d.id,
        name: d.name,
      };
      databasesIds.push(d.id);
      databases[d.id] = database;
      tablesForDatabase[d.id] = [];
      forEach(d.children, (t) => {
        let table = {
          id: t.id,
          name: t.name,
          database: d.name,
          columns: map(t.children, (c) => ({
            ...c,
          })),
          scanned: t.scanned,
        };
        tables[t.id] = table;
        tablesForDatabase[d.id].push(t.id);
      });
    });
    this.setState(
      {
        databasesIds,
        databases,
        tables,
        tablesForDatabase,
        selectedDatabaseId:
          props.selectedDatabase || props.sourceConfig.database || this.state.selectedDatabaseId,
      },
      () => {
        // initialize state from the source being edited
        const { sourceBeingEdited, sourceConfig } = props;

        if (sourceBeingEdited && sourceConfig.table) {
          const { database, table } = sourceConfig;
          const tableId = `${database || databasesIds[0]}/${table}`;

          // Only try to select table from the list if it exists
          if (!isEmpty(tables) && tables[tableId]) {
            // Capture before selectTable queues its own setState({ expandedTableId })
            // so we can restore it afterward and prevent re-probing from collapsing
            // a table the user already has open.
            const currentExpandedTableId = this.state.expandedTableId;
            this.selectTable(tableId);
            const tableConfig = pick(sourceConfig, TABLE_CONFIG_KEYS);
            this.updateTable(tableId, tableConfig);
            if (currentExpandedTableId) {
              this.setState({ expandedTableId: currentExpandedTableId });
            }
          } else {
            /**
             * If the table is not in the list, set the expanded table id to the table id
             */
            this.setState({ expandedTableId: tableId });
          }
        }
      },
    );
  };

  selectDatabase = (dId) => {
    if (dId === this.state.selectedDatabaseId) {
      this.setState(
        {
          selectedDatabaseId: null,
        },
        () => {
          this.props.onTableSelected(null);
        },
      );
    } else {
      const isDatabaseNotFound = !this.state.databases[dId];

      if (isDatabaseNotFound) {
        // database not found in the list, user added from the database input
        this.props.onSourceConfigChanged({
          ...this.props.sourceConfig,
          database: dId,
        });
        this.setState({
          selectedDatabaseId: dId,
        });

        return;
      }

      this.probeNode(dId);
      this.setState(
        {
          selectedDatabaseId: dId,
        },
        () => {
          // Only clear table selection if there's no table in sourceConfig
          // This preserves macro values when editing a source
          if (!this.props.sourceConfig.table) {
            this.props.onTableSelected(null, dId);
          }
        },
      );
    }
  };

  expandTable = (tId) => {
    this.probeNode(tId);
    this.setState({
      expandedTableId: tId,
    });
  };

  onChangeTableInput = (tId) => {
    if (tId.length === 0) {
      this.setState(
        {
          expandedTableId: '',
        },
        () => {
          this.props.onTableSelected(null);
        },
      );
    }

    if (tId) {
      const table = this.state.tables?.[tId];
      if (!table) {
        this.props.onSourceConfigChanged({
          ...this.props.sourceConfig,
          table: tId?.split('/')[1],
        });
        this.setState({
          expandedTableId: tId,
        });
      } else {
        this.probeNode(tId);
        this.setState(
          {
            expandedTableId: tId,
          },
          () => {
            this.props.onTableSelected(table);
          },
        );
      }
    }
  };

  selectTable = (tId) => {
    if (tId.length === 0) {
      this.setState(
        {
          expandedTableId: '',
        },
        () => {
          this.props.onTableSelected(null);
        },
      );
      return;
    }

    if (tId) {
      let table = this.state?.tables?.[tId] || {};
      if (!isEmpty(table)) {
        this.probeNode(tId);
        this.setState(
          {
            expandedTableId: tId,
          },
          () => {
            this.props.onTableSelected(table);
          },
        );
      } else {
        // table not found in the list (could be a macro or user-typed value)
        // Don't clear the table value - it's already in sourceConfig
        this.setState({
          expandedTableId: tId,
        });
      }
    }
  };

  // Tree click handler. In Query mode an explicit click loads the clicked table
  // into the query (regenerate auto-SQL) so the preview refreshes to it — unlike
  // hover/probe, which must never change the preview automatically.
  _onTreeSelectTable = (tId) => {
    const isQuery =
      getEffectiveQueryMode(this.props.queryModeOnly, this.props.sourceConfig) === 'Query';
    if (isQuery && this.state.isCustomQuery) {
      this.setState({ isCustomQuery: false });
    }
    this.selectTable(tId);
  };

  updateTable = (tId, tableConfig) => {
    let table;
    if (tId === (this.props.selectedTable || {}).id) {
      table = this.props.selectedTable;
    } else {
      table = this.state.tables[tId];
    }
    if (table) {
      forEach(tableConfig, (value, key) => {
        if (table[key] === value) {
          delete table[key];
        } else {
          table[key] = value;
        }
      });
    }
    if (!isEmpty(table)) {
      this.props.onTableSelected(table);
    }
  };

  updateSourceConfigField = (name, value) => {
    this.props.onSourceConfigChanged({
      ...this.props.sourceConfig,
      [name]: value,
    });
  };

  probeNode = (nodeId) => {
    const node = this.props.probeDataFlat[nodeId];
    if (!node || node.scanned) return;
    this.props.onRequestProbeTree(false, node);
  };

  onTabChange = (e, value) => {
    this.setState({ mode: value });
    this.props.onSourceConfigChanged({
      ...this.props.sourceConfig,
      db_query_mode: value,
    });
  };

  validateRequiredFields = (configToBeValidate) => {
    const { sourceConfigFieldsList: fields } = this.props;
    const missingFields = getEmptyRequiredFields(fields, configToBeValidate);
    return !missingFields.length;
  };

  dispatchAdvancedModeSave = async (sourceConfigString) => {
    const { nextFormStage, onSaveAdvancedMode, onError } = this.props;

    if (!JSONUtils.isJSON(sourceConfigString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const parsedConfig = JSON.parse(sourceConfigString);
    const { name, description, source_config } = parsedConfig;

    const data = {
      ...source_config,
      name,
      description,
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sourceConfig, onSourceConfigChanged } = this.props;
    if (typeof onSourceConfigChanged !== 'function') return;

    onSourceConfigChanged({ ...sourceConfig, 'ui.mode': UiMode.VIEW });
  };

  getResourceConfig = () => {
    const clonedSourceConfig = structuredClone(this.props.sourceConfig);
    const name = this.props.sourceName || clonedSourceConfig.name || '';
    const description = this.props.sourceDescription || clonedSourceConfig.description || '';

    delete clonedSourceConfig.name;
    delete clonedSourceConfig.description;
    delete clonedSourceConfig.sourceName;
    delete clonedSourceConfig.sourceDescription;

    return {
      name,
      description,
      source_config: clonedSourceConfig,
    };
  };

  onRequestTree = () => {
    if (
      this.props.sourceConfig &&
      this.props.sourceConfig.database &&
      this.props.sourceConfig.table
    ) {
      this.props.onRequestInitialDBProbeTree({
        type: 'table',
        name: this.props.sourceConfig.table,
        parent: this.props.sourceConfig.database,
      });
    } else {
      this.props.onRequestProbeTree();
    }
  };

  // Redesigned two-pane Source Table: persistent schema tree (left) +
  // preview / SQL editor (right). Used for the standard Flex Flow DB source
  // (the API-server / parentDataSet flow keeps its existing layout).
  _renderRedesignedSourceTable = (ctx) => {
    const {
      databasesArray,
      tablesForDatabase,
      tables,
      selectedDatabaseId,
      selectedTable,
      selectedQueryMode,
      query,
      samples,
      isLoadingProbeSample,
      shouldDisableTest,
      onFetchSampleData,
      sourceConfig,
      treeLoading,
      connectionName,
    } = ctx;

    const isQuery = selectedQueryMode === 'Query';
    const database = selectedTable?.database || sourceConfig?.database;
    const tableName = selectedTable?.name || sourceConfig?.table;
    const selectedTableLabel = tableName
      ? database
        ? `${database}.${tableName}`
        : tableName
      : undefined;

    // In Query mode the preview reflects the executed query (its FROM table),
    // independent of what's selected in the tree. In Table mode it reflects the
    // selected table.
    const queriedRef = isQuery ? this._getQueriedTableRef() : '';
    const previewLabel = isQuery ? queriedRef || 'Query result' : selectedTableLabel;
    const previewColumns = isQuery
      ? this._getQueriedTableColumns(queriedRef)
      : selectedTable?.columns || [];

    // The table whose samples are being fetched — used to mark it "recent".
    const exploredTableName = (isQuery ? queriedRef.split('.').pop() : tableName)?.toLowerCase();
    const exploredTableId = exploredTableName
      ? find(tables, (t) => t?.name?.toLowerCase() === exploredTableName)?.id
      : undefined;
    const fetchSamples = () => {
      if (!onFetchSampleData) return;
      this._markRecentTable(exploredTableId);
      onFetchSampleData();
    };
    const showToggle = !this.props.queryModeOnly;

    return (
      <>
        <span id="source" className="hidden-source-element" />
        <div className="db-source-redesign">
          <SchemaTreePanel
            databases={databasesArray}
            tablesForDatabase={tablesForDatabase}
            tables={tables}
            selectedDatabaseId={selectedDatabaseId}
            selectedTableId={selectedTable?.id}
            pinnedTableIds={this.state.pinnedTableIds}
            onSelectDatabase={this.selectDatabase}
            onSelectTable={this._onTreeSelectTable}
            onTogglePin={this._onTogglePin}
            onProbeTable={this._probeTableForPreview}
            loading={treeLoading}
            connectionName={connectionName}
          />

          <div className="db-workspace">
            {showToggle && (
              <div className="db-workspace-toolbar">
                <SourceModeToggle
                  mode={selectedQueryMode}
                  onChange={(value) => this.onTabChange(null, value)}
                />
              </div>
            )}

            <div className="db-workspace-body">
              {selectedQueryMode === 'Query' && (
                <div className="db-workspace-editor">
                  <SqlEditorPanel
                    value={query}
                    isCustom={this.state.isCustomQuery}
                    selectedTableLabel={tableName}
                    canRun={!shouldDisableTest}
                    running={isLoadingProbeSample}
                    onChange={this._onQueryChange}
                    onRun={fetchSamples}
                    onResetToTable={this._onResetQuery}
                    onMount={this._handleSqlEditorMount}
                  />
                </div>
              )}

              <div className="db-workspace-preview">
                <SourceTablePreview
                  columns={previewColumns}
                  samples={samples}
                  loading={isLoadingProbeSample}
                  tableLabel={previewLabel}
                  canFetch={!shouldDisableTest}
                  onFetch={fetchSamples}
                  mode={selectedQueryMode}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  render() {
    let {
      databasesIds,
      databases,
      tables,
      selectedDatabaseId,
      expandedTableId,
      tablesForDatabase,
    } = this.state;
    let {
      sourceConfigFields,
      sourceBeingEdited,
      selectedTable,
      probeData,
      probeDataFlat,
      isLoadingProbeData,
      isLoadingProbeSample,
      connectionMetas,
      selectedConnection,
      sourceConfig,
      probeSample,
      onGetProbeSample,
      onSourceConfigChanged,
      updateFormData,
      onInputChange,
      sourceName,
      sourceDescription,
      sourceConfigFieldsList,
      visibleFieldData,
      parentDataSet,
      parentDataSetSamples,
    } = this.props;

    let databasesArray = map(databasesIds, (i) => databases[i]);
    let selectedQueryMode = getEffectiveQueryMode(this.props.queryModeOnly, sourceConfig);
    let query = sourceConfig.query || '';
    let isApiServerFlow = !!parentDataSet;
    // Redesigned Source Table layout applies to the standard Flex Flow DB
    // source flow. The API-server (parentDataSet) flow keeps its 3-pane layout.
    const useRedesign = !isApiServerFlow;
    let advancedFieldNames = [
      'start.cron',
      'catalog',
      'cloud.credentials.type',
      'cloud.credentials.id',
    ];

    let allFields = sourceConfigFieldsList || sourceConfigFields[selectedConnection];

    // "Perform Database Commit after Read?" is a true/false setting — render it
    // as a radio group (one click) instead of a two-click dropdown.
    forEach(allFields, (field, index) => {
      if (field?.name === 'commit.on.read') {
        allFields[index] = { ...field, inputType: 'radio' };
      }
    });

    // In Query mode, cdc.enabled is not a visible field, it gets stripped from
    // visibleFieldData and may be absent from sourceConfig. commit.on.read has a
    // dependsOn condition that requires cdc.enabled === false. CDC is inherently
    // inactive in Query mode, apply cdc.enabled's default (false) when absent so
    // dependent field visibility evaluates correctly
    const sourceConfigForRendering =
      selectedQueryMode === 'Query' && !('cdc.enabled' in sourceConfig)
        ? { ...sourceConfig, 'cdc.enabled': false }
        : sourceConfig;

    // TODO: CDC:DB revert
    // const allowDatabaseSelection = has(sourceConfig, 'cdc.enabled') && sourceConfig['cdc.enabled'] === true;
    const allowTableSelection = sourceConfig.db_query_mode !== 'Query';
    const allowDatabaseSelection = false;
    let shouldDisableTest = false;
    if (selectedQueryMode === 'Query') {
      shouldDisableTest = isEmpty(query);
    } else {
      // Check sourceConfig.table first (for macros), then selectedTable (for normal flow)
      shouldDisableTest = isEmpty(sourceConfig.table) && isEmpty(selectedTable);
    }

    let allowDBSelectionFieldExist = filter(allFields, (field) =>
      UIUtils.shouldRenderField(field, sourceConfig),
    ).find((field) => field?.name === 'cdc.enabled');
    if (allowDBSelectionFieldExist) {
      advancedFieldNames.push('cdc.enabled', 'cdc.capture.delete', 'cdc.snapshot.enabled');
    }

    const hasDataBoostField =
      selectedConnection === 'gcp_spanner'
        ? !isNil(find(allFields, (field) => field.name === 'enable.spanner.data.boost'))
        : false;

    if (selectedQueryMode === 'Default') {
      advancedFieldNames.push(
        'mode',
        'incrementing.column.name',
        'incrementing.load.from',
        'timestamp.column.name',
        'timestamp.load.from',
        //'commit.on.read'
      );

      if (hasDataBoostField) {
        advancedFieldNames.push('enable.spanner.data.boost');
      }

      let columnOptions = map(selectedTable && selectedTable.columns, (c) => ({
        label: c.name,
        value: c.name,
      }));
      forEach(allFields, (field, index) => {
        if (['incrementing.column.name', 'timestamp.column.name'].includes(field.name)) {
          allFields[index] = {
            ...field,
            values: columnOptions,
            inputType: 'select',
          };
        }
      });
    } else {
      if (hasDataBoostField) {
        advancedFieldNames.push('enable.spanner.data.boost');
      }
      advancedFieldNames.push('commit.on.read');
    }

    advancedFieldNames.push(...Constants.HC_SOURCE_FIELD_NAMES);

    let advancedFields = map(
      filter(
        allFields,
        (field) =>
          advancedFieldNames.includes(field.name) ||
          advancedFieldNames.includes(field.fieldName) ||
          field.type === 'runtime_selection',
      ),
      (field) => {
        // disable lower HC scale factors
        if (field.name === 'hc_scale_factor') {
          const hcScaleFactorOptions = UIUtils.disableInvalidHCOptions(
            field.options,
            sourceBeingEdited &&
              sourceBeingEdited.source_config &&
              sourceBeingEdited.source_config['hc_scale_factor'],
          );

          return { ...field, options: hcScaleFactorOptions };
        }

        return field;
      },
    );

    const fieldsWithoutScheduling = advancedFields.filter(
      (field) => field.name !== 'start.cron' && field.type !== 'runtime_selection',
    );

    const handleLoadableMonacoEditorMount = (editor, monaco) => {
      let suggestions = [];

      const { languages } = monaco;
      const { CompletionItemKind, registerCompletionItemProvider } = languages;

      if (selectedQueryMode === 'Query') {
        const mapper = (x) => ({
          label: x.name,
          kind: CompletionItemKind.Value,
          insertText: x.name,
        });

        suggestions = [...map(selectedTable?.columns, mapper), ...map(tables, mapper)];
      }

      if (!suggestions.length) return;

      return [
        registerCompletionItemProvider('sql', {
          provideCompletionItems(model, position) {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            return {
              suggestions: suggestions.map((suggestion) => ({
                ...suggestion,
                range,
              })),
            };
          },
        }),
      ];
    };

    const handleLoadableMonacoEditorChange = debounce((query) => {
      this.updateSourceConfigField('query', query);
    }, 200);

    const isLoadingTree = isEmpty(probeData) && isLoadingProbeData;
    const meta = connectionMetas[selectedConnection];
    const samples = this.state.samplesData;
    const cronField = advancedFields.find((o) => o?.name === 'start.cron');
    const onFetchSampleData = shouldDisableTest ? null : onGetProbeSample;

    const filteredAdvancedFields =
      advancedFields.filter((o) => o.name !== 'start.cron' && o.type !== 'runtime_selection') || [];
    const isHCEnabled = UIUtils.isHCEnabled(
      this.props.currentOrg?.id,
      this.props.selectedConnection,
    );

    const fields = filter(
      filteredAdvancedFields,
      (o) =>
        !(
          o.name === 'path' ||
          o.name === 'output.dir.name.pattern' ||
          (!isHCEnabled && Constants.HC_SOURCE_FIELD_NAMES.includes(o.name))
        ),
    );

    const sections = UIUtils.getActiveSections(fields, sourceConfigForRendering);

    let dynamicSections = sections.map((section) => ({
      label: section,
      id: UIUtils.getSectionElementId(section),
    }));
    if (this.props.queryModeOnly) {
      dynamicSections = dynamicSections.filter((s) => s.label !== 'Post Read Settings');
    }

    const navOptions = [
      { label: 'Source Table', id: 'source' },
      ...dynamicSections,
      // Forward-looking: DB runtime selection is wired but gated off by the
      // `database` matrix entry in UI_ENABLE_RUNTIME_SELECTION (defaults false).
      // Capsule does not yet support database sources; flip the flag only once
      // the backend ingest pipeline supports it. No further UI work needed then.
      ...(!isApiServerFlow &&
      isSourceFormRuntimeSelectionEnabled(
        getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS'),
        getFeatureFlagValue('UI_ENABLE_RUNTIME_SELECTION'),
        CONNECTION_CATEGORY_DATABASE,
        this.props.flowType,
      ) &&
      this.state.runtimeSectionVisible
        ? [{ label: 'Select Runtime', id: 'select-runtime-section' }]
        : []),
      ...(!!cronField ? [{ label: 'Schedule Ingestion', id: 'section-scheduling' }] : []),
    ];

    const resourceConfig = this.getResourceConfig();

    return (
      <div style={{ height: '100%' }}>
        <DataSourceDetails
          name={sourceName}
          description={sourceDescription}
          onInputChange={onInputChange}
          leftView={
            <HeaderMetaConfiguration
              enableInputFields={false}
              isDestination={false}
              onChange={onInputChange}
              connection={selectedConnection}
              sourceOrDestinationChipChildren={
                parentDataSet ? null : (
                  <ResourcePopoverMenu
                    resourceConfig={resourceConfig}
                    connectorType={selectedConnection}
                    jsonConfigOnSave={this.dispatchAdvancedModeSave}
                    jsonConfigOnClose={this.props.goBack}
                    onSwitchUiMode={this.onSwitchUiMode}
                    resourcePersisted={!!this.props.sourceBeingEdited?.id}
                  />
                )
              }
            />
          }
        />

        <div
          className={classnames({
            'api-server-target-horizontal-layout api-server-target-database-configurator':
              !!parentDataSet,
          })}
        >
          <div className="database-configurator-container">
            {!isApiServerFlow && !this.props.queryModeOnly && !useRedesign && (
              <div className="tabs-and-button-container">
                <div className="tabs-title-container">
                  <Typography variant="body2">Schema selection mode</Typography>
                  <Tooltip
                    placement="top"
                    title="Table Mode: Select a database and table from the list of available tables. Query Mode: Write a custom query to fetch data from the database."
                  >
                    <InfoIcon className="tabs-title-icon" />
                  </Tooltip>
                </div>

                {allFields?.find?.((field) => field.name === 'db_query_mode') ? (
                  <SegmentedControl
                    controls={controls}
                    value={this.state.mode}
                    onControlChange={this.onTabChange}
                    centered
                    defaultValue={this.state.mode}
                  />
                ) : null}
              </div>
            )}

            <div
              className="advanced-configuration-container"
              style={{
                height:
                  isApiServerFlow || this.props.queryModeOnly || useRedesign
                    ? '100%'
                    : 'calc(100% - 50px)',
              }}
            >
              <div
                className={classnames('database-source-configurator', {
                  'is-query-mode': selectedQueryMode === 'Query',
                })}
              >
                <SearchableProvider>
                  {(onChangeSearch) => (
                    <NavScroller
                      style={{ height: '100%' }}
                      navOptions={parentDataSet ? [] : navOptions}
                      isColumnLayout={!!parentDataSet}
                    >
                      <NavScrollerContext.Consumer>
                        {(consumer) => {
                          return (
                            <div className="nav-scroller-container">
                              {useRedesign ? (
                                this._renderRedesignedSourceTable({
                                  databasesArray,
                                  tablesForDatabase,
                                  tables,
                                  selectedDatabaseId,
                                  selectedTable,
                                  selectedQueryMode,
                                  query,
                                  samples,
                                  isLoadingProbeSample,
                                  shouldDisableTest,
                                  onFetchSampleData,
                                  sourceConfig,
                                  treeLoading:
                                    !this.props.probeError &&
                                    (isNil(probeData) || isLoadingProbeData),
                                  connectionName: meta?.displayName,
                                })
                              ) : (
                                <div className="table-list-and-data-samples-container">
                                  <ResizableHorizontalLayout
                                    initialSizes={
                                      parentDataSet ? ['25%', '40%', '35%'] : ['75%', '25%']
                                    }
                                    minSizes={parentDataSet ? [340, 400, 340] : [400, 320]}
                                  >
                                    {parentDataSet && (
                                      <div className="parent-data-set-container">
                                        <DataSetDataBrowserContainer
                                          data={parentDataSet}
                                          defaultViewMode={SchemaEditorViewModeType.CODE}
                                          compactHeader
                                          darkEditor
                                          allowEditing
                                          onSamplesChanged={this._handleSamplesChanged}
                                        />
                                      </div>
                                    )}
                                    <Box
                                      className={classnames({
                                        'source-table-box': true,
                                        'left-part': true,
                                        'left-part-with-samples': true,
                                        'left-part-with-samples-focused':
                                          consumer?.activeScroll === 'source',
                                      })}
                                      title={
                                        <div className="source-table-box-title">
                                          <Typography
                                            variant="subtitle2"
                                            style={{ fontWeight: 'var(--font-weight-medium)' }}
                                          >
                                            {selectedQueryMode === 'Query'
                                              ? 'Query'
                                              : 'Source Table'}
                                          </Typography>
                                          <Typography
                                            variant="body3"
                                            className="source-table-box-subtitle"
                                          >
                                            {selectedQueryMode === 'Query'
                                              ? 'Specify the query to fetch data from the database.'
                                              : 'Specify the source table where the system retrieves data for processing.'}
                                          </Typography>
                                        </div>
                                      }
                                      rightView={
                                        <>
                                          <span id="source" className="hidden-source-element" />
                                          {selectedQueryMode === 'Query' ? (
                                            <Button
                                              disabled={shouldDisableTest}
                                              onClick={onFetchSampleData}
                                              className={classnames('test-connection-button', {
                                                loading: isLoadingProbeSample,
                                              })}
                                            >
                                              {isLoadingProbeSample ? (
                                                <CircularProgress
                                                  size={19}
                                                  sx={{
                                                    color: 'var(--color-danger-700)',
                                                  }}
                                                />
                                              ) : (
                                                'Fetch Sample Data'
                                              )}
                                            </Button>
                                          ) : (
                                            <ExpandableSearchBar onChange={onChangeSearch} />
                                          )}
                                        </>
                                      }
                                    >
                                      {selectedQueryMode === 'Default' && (
                                        <SelectedDatabaseInputs
                                          selectedDatabaseId={
                                            sourceConfig.database || selectedDatabaseId
                                          }
                                          selectedTableId={
                                            sourceConfig.table
                                              ? `${sourceConfig.database || ''}/${sourceConfig.table}`
                                              : selectedTable?.id || ''
                                          }
                                          selectDatabase={this.selectDatabase}
                                          selectTable={this.onChangeTableInput}
                                          databasesIds={databasesIds}
                                          isApiServerFlow={isApiServerFlow}
                                          parentDataSet={parentDataSet}
                                          parentDataSetSamples={parentDataSetSamples}
                                          rightAction={
                                            <div className="refresh-buttons-container">
                                              <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={shouldDisableTest}
                                                onClick={
                                                  shouldDisableTest ? null : onGetProbeSample
                                                }
                                                className={cx('test-connection-button', {
                                                  loading: isLoadingProbeSample,
                                                })}
                                              >
                                                {isLoadingProbeSample ? (
                                                  <CircularProgress
                                                    size={19}
                                                    sx={{
                                                      color: 'var(--color-danger-700)',
                                                    }}
                                                  />
                                                ) : (
                                                  'Fetch Sample Data'
                                                )}
                                              </Button>
                                              {this.props.isLoadingProbeData ? (
                                                <div className="refresh-loader-container">
                                                  <CircularProgress
                                                    size={19}
                                                    sx={{
                                                      color: 'currentColor',
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <RefreshIcon
                                                  className="refresh-icon"
                                                  fontSize="large"
                                                  onClick={() => this.props.onRequestProbeTree()}
                                                />
                                              )}
                                            </div>
                                          }
                                        />
                                      )}
                                      {selectedQueryMode === 'Default' &&
                                        !this.props.probeError &&
                                        !isLoadingTree && (
                                          <DatabaseList
                                            databases={databasesArray}
                                            selectedDatabaseId={selectedDatabaseId}
                                            selectDatabase={this.selectDatabase}
                                            tablesForDatabase={tablesForDatabase}
                                            tables={tables}
                                            allowTableSelection={allowTableSelection}
                                            selectTable={this.selectTable.bind(this)}
                                            expandedTableId={expandedTableId}
                                            expandTable={this.expandTable.bind(this)}
                                            probeDataFlat={probeDataFlat}
                                            selectedTable={selectedTable || {}}
                                            allowDatabaseSelection={allowDatabaseSelection}
                                          />
                                        )}
                                      {selectedQueryMode === 'Query' && (
                                        <div className="source-table-query-container">
                                          <ResizableVerticalLayout
                                            {...ResizableVerticalLayoutConfig}
                                          >
                                            <div className="input-row query-editor-row">
                                              <div className="input-holder">
                                                <div style={{ flexGrow: 1 }}>
                                                  <LoadableMonacoEditor
                                                    language="sql"
                                                    theme="dark"
                                                    value={query}
                                                    height="100%"
                                                    onMount={handleLoadableMonacoEditorMount}
                                                    onChange={handleLoadableMonacoEditorChange}
                                                    options={loadableMonacoEditorOptions}
                                                  />
                                                </div>
                                              </div>
                                            </div>

                                            <div>
                                              {this.props.probeError ? (
                                                <Stack padding="40px">
                                                  <EmptyView
                                                    title="There was an error loading resources"
                                                    actionTitle="Try Again"
                                                    actionProps={{
                                                      onClick: this.onRequestTree,
                                                    }}
                                                  />
                                                </Stack>
                                              ) : isLoadingTree ? (
                                                <div className="select-bucket-tree-list-loading">
                                                  <CircularProgress />
                                                  Connecting to {meta?.displayName}...
                                                </div>
                                              ) : (
                                                <DatabaseList
                                                  databases={databasesArray}
                                                  selectedDatabaseId={selectedDatabaseId}
                                                  selectDatabase={this.selectDatabase}
                                                  tablesForDatabase={tablesForDatabase}
                                                  tables={tables}
                                                  expandedTableId={expandedTableId}
                                                  disableSelection={true}
                                                  probeDataFlat={probeDataFlat}
                                                  expandTable={this.expandTable}
                                                  allowDatabaseSelection={allowDatabaseSelection}
                                                />
                                              )}
                                            </div>
                                          </ResizableVerticalLayout>
                                        </div>
                                      )}
                                      {selectedQueryMode === 'Default' &&
                                        (this.props.probeError || isLoadingTree) && (
                                          <>
                                            {this.props.probeError ? (
                                              <Stack padding="40px">
                                                <EmptyView
                                                  title="There was an error loading resources"
                                                  actionTitle="Try Again"
                                                  actionProps={{
                                                    onClick: this.onRequestTree,
                                                  }}
                                                />
                                              </Stack>
                                            ) : (
                                              <div className="select-bucket-tree-list-loading">
                                                <CircularProgress />
                                                Connecting to {meta?.displayName}
                                                ...
                                              </div>
                                            )}
                                          </>
                                        )}
                                    </Box>

                                    <div className="data-selection-container right-part">
                                      <span />

                                      <DatabaseSamples
                                        samples={samples}
                                        lastUpdated={get(probeSample, '[0].sampleLastUpdatedAt')}
                                        connectionCategory={CONNECTION_CATEGORY_DATABASE}
                                      />
                                    </div>
                                  </ResizableHorizontalLayout>
                                </div>
                              )}

                              {dynamicSections.map((section) => {
                                const sectionClassname =
                                  consumer?.activeScroll === section.id
                                    ? 'data-selection-container data-selection-container-focused'
                                    : 'data-selection-container';

                                return (
                                  <div
                                    key={section?.id}
                                    id={section.id}
                                    className={sectionClassname}
                                  >
                                    <AdvancedConfiguration
                                      selectedConnection={selectedConnection}
                                      sourceConfigFields={filter(fieldsWithoutScheduling, {
                                        section: section.label,
                                      })}
                                      sourceConfig={sourceConfigForRendering}
                                      onSourceConfigChanged={onSourceConfigChanged}
                                      resourceMode="source"
                                      connectionType={selectedConnection}
                                      connectionCategory={CONNECTION_CATEGORY_DATABASE}
                                      updateFormData={updateFormData}
                                      expandTable={this.expandTable}
                                    />
                                  </div>
                                );
                              })}

                              {!isApiServerFlow &&
                                isSourceFormRuntimeSelectionEnabled(
                                  getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS'),
                                  getFeatureFlagValue('UI_ENABLE_RUNTIME_SELECTION'),
                                  CONNECTION_CATEGORY_DATABASE,
                                  this.props.flowType,
                                ) &&
                                this.state.runtimeSectionVisible && (
                                  <div
                                    id="select-runtime-section"
                                    aria-label="section-container"
                                    className={classnames({
                                      runtime_selection_section_card: true,
                                      runtime_selection_section_card_focused:
                                        consumer?.activeScroll === 'select-runtime-section',
                                    })}
                                  >
                                    <h2 className="runtime_selection_section_heading">
                                      {this.props.runtimeSelectionDisabled
                                        ? 'Selected Runtime'
                                        : 'Select Runtime'}
                                    </h2>
                                    <RuntimeSelectionDescription
                                      readOnly={this.props.runtimeSelectionDisabled}
                                    />
                                    <div className="runtime_selection_section_body">
                                      <RuntimeSelection
                                        flowType={this.props.flowType}
                                        connectionType={CONNECTION_CATEGORY_DATABASE}
                                        resourceConfig={this.props.sourceConfig || {}}
                                        onSelectRuntime={this.props.onSelectRuntime}
                                        disabled={this.props.runtimeSelectionDisabled}
                                        selectedClusterId={this.props.selectedClusterId}
                                        onVisibilityChange={this._handleRuntimeVisibilityChange}
                                      />
                                    </div>
                                  </div>
                                )}

                              {!parentDataSet && (
                                <div id="section-scheduling">
                                  <MonitoringInterval
                                    field={cronField}
                                    source={this.props.sourceBeingEdited}
                                    config={sourceConfigForRendering}
                                    monitorIntervalContainerClassname={classnames({
                                      scheduling: true,
                                      'scheduling-focused':
                                        consumer?.activeScroll === 'section-scheduling',
                                    })}
                                    onConfigChanged={onSourceConfigChanged}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        }}
                      </NavScrollerContext.Consumer>
                    </NavScroller>
                  )}
                </SearchableProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DatabaseConfigurator;
